import React from "react";
import { Dropdown } from "react-bootstrap";
import { Link } from "@tanstack/react-router";
import { useLogout } from "../../hooks/useLogout";
import { ThemeToggle } from "./ThemeToggle";

interface UserMenuProps {
  user: {
    name?: string;
    username?: string;
    givenName?: string;
  };
  collapsed?: boolean;
  onToggle?: (isOpen: boolean) => void;
  onNavigate?: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  collapsed = false,
  onToggle,
  onNavigate,
}) => {
  const logout = useLogout();

  // Format name as "First Last" (Azure AD often returns "Last, First")
  const formatName = (name: string) => {
    if (name.includes(", ")) {
      const [last, first] = name.split(", ");
      return `${first} ${last}`;
    }
    return name;
  };

  const rawName = user.name || user.givenName || user.username || "User";
  const displayName = formatName(rawName);
  const username = user.username || "Florida Department of Health";

  const getInitials = (name: string) => {
    if (user.username && user.username.includes(".")) {
      const location = user.username.indexOf(".") + 1;
      return (
        user.username.substring(0, 1) +
        user.username.substring(location, location + 1)
      );
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    const logoutRequest = {};
    logout(logoutRequest);
  };

  // Collapsed mode: render dropdown with nav-link styled items
  if (collapsed) {
    return (
      <Dropdown
        align="end"
        className="cursor-pointer user-menu"
        onToggle={onToggle}
      >
        <Dropdown.Toggle
          id="user-dropdown-collapsed"
          className="nav-link shadow-none text-decoration-none d-flex align-items-center btn-no-effects"
          // style={{ padding: "8px" }}
        >
          <div className="d-flex align-items-center">
            <div className="avatar me-2">{getInitials(displayName)}</div>
            <div className="text-start">
              {/* <div style={{ lineHeight: "1.2" }}> */}
              <div className="text-truncate" title={displayName}>
                {displayName}
                {/* </div> */}
              </div>
            </div>
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu className="collapsed-user-menu">
          <div className="nav-like">
            <div className="collapsed-theme-toggle">
              <ThemeToggle inline />
            </div>
            <Dropdown.Item as={Link} to="/profile" onClick={onNavigate}>
              Profile
            </Dropdown.Item>
            <Dropdown.Item
              href="https://floridahealth.gov/environmental-health"
              target="_blank"
              rel="noreferrer"
              onClick={onNavigate}
            >
              Help & Documentation
            </Dropdown.Item>
            <Dropdown.Item
              onClick={(e) => {
                onNavigate?.();
                handleLogout(e);
              }}
            >
              Sign Out
            </Dropdown.Item>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  return (
    <Dropdown align="end" className="cursor-pointer user-menu">
      <Dropdown.Toggle
        id="user-dropdown"
        className="nav-link shadow-none text-decoration-none d-flex align-items-center btn-no-effects pe-0"
      >
        <div className="d-flex align-items-center">
          <div className="avatar me-2">{getInitials(displayName)}</div>

          {/* Show name on medium+ screens */}
          <div className="d-none d-md-block text-start user-info">
            <div style={{ lineHeight: "1.2" }}>
              <div className="text-truncate text-body" title={displayName}>
                {displayName}
              </div>
              {/* Show email on xl+ screens */}
              <div
                className="small opacity-75 d-none d-xl-block text-truncate"
                title={username}
              >
                {username}
              </div>
            </div>
          </div>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="border-0">
        <Dropdown.Header>Theme</Dropdown.Header>
        <div className="expanded-theme-toggle px-3 py-2">
          <ThemeToggle inline />
        </div>

        <Dropdown.Item as={Link} to="/profile">
          Profile
        </Dropdown.Item>

        <Dropdown.Item
          href="https://floridahealth.gov/environmental-health"
          target="_blank"
          rel="noreferrer"
        >
          Help & Documentation
        </Dropdown.Item>

        <Dropdown.Item onClick={handleLogout}>Sign Out</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};
